/**
 * Clinical Text Formatter
 *
 * Intelligently formats clinical text (SOAP notes, imaging reports) for display.
 * Detects section headers, lists, vital signs, and other structured content.
 */

export type FormattedElementType =
  | 'header'
  | 'paragraph'
  | 'numbered-list'
  | 'bullet-list'
  | 'vitals'
  | 'line-break';

export interface VitalSign {
  type: string;
  value: string;
  unit: string;
}

export interface FormattedElement {
  type: FormattedElementType;
  content?: string;
  items?: string[];
  signs?: VitalSign[];
  key?: string;
}

// Regex patterns for detecting structured content
const PATTERNS = {
  // SOAP section headers (SUBJECTIVE:, OBJECTIVE:, etc.)
  sectionHeader: /^([A-Z][A-Z\s]+):\s*(.*)$/,

  // Numbered lists (1. or 1) at start of line)
  numberedList: /^(\d+)[.)]\s+(.+)$/,

  // Bullet points (-, *, •)
  bulletList: /^[-*•]\s+(.+)$/,

  // Vital signs patterns
  heartRate: /\bHR:?\s*(\d+)(?:\s*bpm)?\b/i,
  bloodPressure: /\bBP:?\s*(\d+)\/(\d+)(?:\s*mmHg)?\b/i,
  respiratoryRate: /\bRR:?\s*(\d+)(?:\s*(?:breaths?\/min)?)?\b/i,
  oxygenSat: /\bSpO2:?\s*(\d+)%?\b/i,
  temperature: /\bTemp(?:erature)?:?\s*(\d+\.?\d*)\s*°?C\b/i,
  weight: /\bWeight:?\s*(\d+\.?\d*)\s*kg\b/i,

  // Check if line contains "Vital Signs:"
  vitalSignsLabel: /Vital\s+Signs:/i,
};

/**
 * Extract vital signs from a text line
 */
function extractVitalSigns(text: string): VitalSign[] {
  const signs: VitalSign[] = [];

  // Heart Rate
  const hrMatch = text.match(PATTERNS.heartRate);
  if (hrMatch) {
    signs.push({ type: 'HR', value: hrMatch[1], unit: 'bpm' });
  }

  // Blood Pressure
  const bpMatch = text.match(PATTERNS.bloodPressure);
  if (bpMatch) {
    signs.push({ type: 'BP', value: `${bpMatch[1]}/${bpMatch[2]}`, unit: 'mmHg' });
  }

  // Respiratory Rate
  const rrMatch = text.match(PATTERNS.respiratoryRate);
  if (rrMatch) {
    signs.push({ type: 'RR', value: rrMatch[1], unit: 'breaths/min' });
  }

  // Oxygen Saturation
  const o2Match = text.match(PATTERNS.oxygenSat);
  if (o2Match) {
    signs.push({ type: 'SpO2', value: o2Match[1], unit: '%' });
  }

  // Temperature
  const tempMatch = text.match(PATTERNS.temperature);
  if (tempMatch) {
    signs.push({ type: 'Temp', value: tempMatch[1], unit: '°C' });
  }

  // Weight
  const weightMatch = text.match(PATTERNS.weight);
  if (weightMatch) {
    signs.push({ type: 'Weight', value: weightMatch[1], unit: 'kg' });
  }

  return signs;
}

/**
 * Main function: analyzes clinical text and returns structured elements
 */
export function analyzeAndFormatClinicalText(text: string): FormattedElement[] {
  if (!text || text.trim().length === 0) {
    return [];
  }

  const elements: FormattedElement[] = [];
  const lines = text.split('\n');

  let currentListItems: string[] = [];
  let currentListType: 'numbered' | 'bullet' | null = null;
  let lineIndex = 0;

  const flushCurrentList = () => {
    if (currentListItems.length > 0 && currentListType) {
      elements.push({
        type: currentListType === 'numbered' ? 'numbered-list' : 'bullet-list',
        items: [...currentListItems],
        key: `list-${lineIndex}`,
      });
      currentListItems = [];
      currentListType = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    lineIndex = i;

    // Skip empty lines (but preserve as line breaks)
    if (trimmedLine.length === 0) {
      flushCurrentList();
      if (elements.length > 0 && elements[elements.length - 1].type !== 'line-break') {
        elements.push({ type: 'line-break', key: `br-${i}` });
      }
      continue;
    }

    // Check for section headers (SUBJECTIVE:, OBJECTIVE:, etc.)
    const headerMatch = trimmedLine.match(PATTERNS.sectionHeader);
    if (headerMatch && headerMatch[1].length > 3) {
      flushCurrentList();
      const header = headerMatch[1];
      const content = headerMatch[2];

      elements.push({
        type: 'header',
        content: header,
        key: `header-${i}`,
      });

      // If there's content after the header, add it as a paragraph
      if (content.trim().length > 0) {
        elements.push({
          type: 'paragraph',
          content: content.trim(),
          key: `header-content-${i}`,
        });
      }
      continue;
    }

    // Check for vitals line
    const vitals = extractVitalSigns(trimmedLine);
    if (vitals.length >= 2 && PATTERNS.vitalSignsLabel.test(trimmedLine)) {
      flushCurrentList();
      // Remove "Vital Signs:" label from content
      const vitalContent = trimmedLine.replace(PATTERNS.vitalSignsLabel, '').trim();
      elements.push({
        type: 'vitals',
        signs: vitals,
        content: vitalContent,
        key: `vitals-${i}`,
      });
      continue;
    }

    // Check for numbered list
    const numberedMatch = trimmedLine.match(PATTERNS.numberedList);
    if (numberedMatch) {
      if (currentListType !== 'numbered') {
        flushCurrentList();
        currentListType = 'numbered';
      }
      currentListItems.push(numberedMatch[2]);
      continue;
    }

    // Check for bullet list
    const bulletMatch = trimmedLine.match(PATTERNS.bulletList);
    if (bulletMatch) {
      if (currentListType !== 'bullet') {
        flushCurrentList();
        currentListType = 'bullet';
      }
      currentListItems.push(bulletMatch[1]);
      continue;
    }

    // Regular paragraph
    flushCurrentList();
    elements.push({
      type: 'paragraph',
      content: trimmedLine,
      key: `para-${i}`,
    });
  }

  // Flush any remaining list
  flushCurrentList();

  return elements;
}

/**
 * Simple helper to convert plain text to formatted elements (fallback)
 */
export function formatPlainText(text: string): FormattedElement[] {
  if (!text) return [];

  return text.split('\n\n').map((para, idx) => ({
    type: 'paragraph' as FormattedElementType,
    content: para.trim(),
    key: `para-${idx}`,
  }));
}
